import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Flex,
  Select,
  Spin,
} from 'antd';
import { getPosHubLocationSettings, patchPosHubLocationSettings } from 'api/poshub';
import { POSHUB_WEEKDAYS } from './constants';
import { StyledSettingsTab } from './SettingsTab.styles';
import PosHubWeeklyHoursGrid from './PosHubWeeklyHoursGrid';
import { formatPosHubTimeForApi, parsePosHubTimeString } from './poshubTimeFormat';

const WEEKDAY_OPTIONS = POSHUB_WEEKDAYS.map((day) => ({
  value: day,
  label: day.charAt(0) + day.slice(1).toLowerCase(),
}));

function makeEmptySchedule() {
  return POSHUB_WEEKDAYS.reduce((acc, day) => {
    acc[day] = { closed: true, timePeriods: [] };
    return acc;
  }, {});
}

function scheduleFromApiBusinessHours(hoursArray) {
  const byDay = (hoursArray || []).reduce((acc, h) => {
    if (h?.weekday) acc[h.weekday] = h;
    return acc;
  }, {});
  return POSHUB_WEEKDAYS.reduce((acc, day) => {
    const h = byDay[day];
    const periods = Array.isArray(h?.timePeriods) ? h.timePeriods : [];
    const valid = periods
      .filter((p) => p?.startTime && p?.endTime)
      .map((p) => ({
        startTime: formatPosHubTimeForApi(parsePosHubTimeString(p.startTime)) || p.startTime,
        endTime: formatPosHubTimeForApi(parsePosHubTimeString(p.endTime)) || p.endTime,
      }));
    if (valid.length) {
      acc[day] = { closed: false, timePeriods: valid };
    } else {
      acc[day] = { closed: true, timePeriods: [] };
    }
    return acc;
  }, {});
}

function apiBusinessHoursFromSchedule(schedule) {
  return POSHUB_WEEKDAYS.map((day) => {
    const s = schedule[day];
    const periods = s?.closed
      ? []
      : (s?.timePeriods || [])
        .filter((p) => p?.startTime && p?.endTime)
        .map((p) => ({
          startTime: formatPosHubTimeForApi(parsePosHubTimeString(p.startTime)) || p.startTime,
          endTime: formatPosHubTimeForApi(parsePosHubTimeString(p.endTime)) || p.endTime,
        }));
    return { weekday: day, timePeriods: periods };
  });
}

function SettingsTab({ restaurantId }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [schedule, setSchedule] = useState(makeEmptySchedule);
  const [copyFromDay, setCopyFromDay] = useState(null);

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    setError(null);
    getPosHubLocationSettings(restaurantId)
      .then((data) => {
        const hours = data?.data?.businessHours || data?.businessHours;
        if (Array.isArray(hours) && hours.length) {
          setSchedule(scheduleFromApiBusinessHours(hours));
        } else {
          setSchedule(makeEmptySchedule());
        }
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || 'Could not load opening hours from the integration.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [restaurantId]);

  const handleSave = async () => {
    if (!restaurantId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const businessHours = apiBusinessHoursFromSchedule(schedule);
      const payload = { businessHours };
      const res = await patchPosHubLocationSettings(restaurantId, payload);
      const hours = res?.data?.businessHours || res?.businessHours;
      if (Array.isArray(hours) && hours.length) {
        setSchedule(scheduleFromApiBusinessHours(hours));
      }
      setSuccess('Opening hours saved to the POS integration and mirrored to Nibble (merchant location hours).');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save opening hours to the integration.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <StyledSettingsTab style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card className="poshub-settings-card" title="Default opening hours">
        {error && (
          <Alert
            type="warning"
            message={error}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {success && (
          <Alert
            type="success"
            message={success}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Alert
              type="info"
              showIcon
              message="Default opening hours for this integrated location. Menus without custom availability inherit these hours."
              style={{ marginBottom: 4 }}
            />
            <Flex gap="small" align="center" wrap="wrap">
              <Select
                placeholder="Select day to copy from"
                value={copyFromDay}
                onChange={setCopyFromDay}
                style={{ minWidth: 200, fontSize: 13 }}
                size="middle"
                options={WEEKDAY_OPTIONS}
              />
              <Button
                size="middle"
                style={{ fontSize: 13 }}
                onClick={() => {
                  if (!copyFromDay) return;
                  const source = schedule[copyFromDay];
                  const toCopy = (source?.timePeriods || []).filter((p) => p?.startTime && p?.endTime);
                  if (!toCopy.length && !source?.closed) return;
                  setSchedule((prev) => {
                    const next = { ...prev };
                    POSHUB_WEEKDAYS.forEach((d) => {
                      if (d === copyFromDay) return;
                      next[d] = {
                        closed: source?.closed ?? true,
                        timePeriods: toCopy.length ? toCopy.map((p) => ({ ...p })) : [],
                      };
                    });
                    return next;
                  });
                }}
                disabled={!copyFromDay}
              >
                Copy to all days
              </Button>
            </Flex>
            <PosHubWeeklyHoursGrid value={schedule} onChange={setSchedule} />
            <Flex justify="flex-end">
              <Button type="primary" onClick={handleSave} loading={saving} disabled={loading}>
                Save
              </Button>
            </Flex>
          </div>
        )}
      </Card>
    </StyledSettingsTab>
  );
}

export default SettingsTab;
